import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MatBottomSheetRef, MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { OperatorNoteDialogComponent as OperatorNoteDialogComponent, OperatorNoteDialogInput } from '../operator-note-dialog/operator-note-dialog.component';
import { ApiService } from 'src/app/services/api.service';
import { OperatorNote } from 'src/models/task-event';
import { TimeBlockEditorDialogComponent, TimeBlockEditorDialogInput, TimeBlockEditorDialogResult } from '../time-block-editor-dialog/time-block-editor-dialog.component';
import * as moment from 'moment';
import { LinkedTimeBlock } from '../review.component';
import { formatMinutesAsDuration } from 'src/util/formatters';

export interface TimeBlockBottomSheetInput {
  timeBlock: LinkedTimeBlock,
  onTimeBlockEdited: () => Promise<void>,
}

@Component({
  selector: 'app-time-block-bottom-sheet',
  templateUrl: './time-block-bottom-sheet.component.html',
  styleUrls: ['./time-block-bottom-sheet.component.scss']
})
export class TimeBlockBottomSheetComponent implements OnInit {
  constructor(
    private changeDetector: ChangeDetectorRef,
    private bottomSheetRef: MatBottomSheetRef<TimeBlockBottomSheetComponent>,
    @Inject(MAT_BOTTOM_SHEET_DATA) public input: TimeBlockBottomSheetInput,
    private dialog: MatDialog,
    private apiService: ApiService,
  ) { }

  get formattedStartTime(): string {
    return moment(this.input.timeBlock.startTime).format('LTS');
  }
  get formattedEndTime(): string | null {
    return this.input.timeBlock.endTime && moment(this.input.timeBlock.endTime).format('LTS');
  }
  get formattedDuration(): string { return this.input.timeBlock.durationInMinutes && formatMinutesAsDuration(this.input.timeBlock.durationInMinutes, 2) || ''; }

  get hasNote(): boolean { return this.input.timeBlock.operatorNoteReason !== null; }
  get isEditable(): boolean { return this.input.timeBlock.isActive; }

  ngOnInit(): void {
  }

  editOperatorNote = (): MatDialogRef<OperatorNoteDialogComponent, OperatorNote> => {
    const handle = this.dialog.open<OperatorNoteDialogComponent, OperatorNoteDialogInput, OperatorNote>(
      OperatorNoteDialogComponent,
      {
        data: { taskName: this.input.timeBlock.parentTask.name, operatorNote: this.input.timeBlock, mode: 'edit' },
      });

    handle.afterClosed().subscribe((result?: OperatorNote) => {
      if (result) {
        this.input.timeBlock.operatorNoteReason = result.operatorNoteReason;
        this.input.timeBlock.operatorNote = result.operatorNote;
        this.apiService.saveOperatorNote(this.input.timeBlock.startTaskEventId, result);
      }
    });
    return handle;
  }

  removeOperatorNote = (): MatDialogRef<OperatorNoteDialogComponent, OperatorNote> => {
    const handle = this.dialog.open<OperatorNoteDialogComponent, OperatorNoteDialogInput, OperatorNote>(
      OperatorNoteDialogComponent,
      {
        data: { taskName: this.input.timeBlock.parentTask.name, operatorNote: this.input.timeBlock, mode: 'remove' },
      });

    handle.afterClosed().subscribe((result?: OperatorNote) => {
      if (result) {
        this.input.timeBlock.operatorNoteReason = null;
        this.input.timeBlock.operatorNote = null;
        this.apiService.saveOperatorNote(this.input.timeBlock.startTaskEventId, null);
      }
    });
    return handle;
  }

  editTimeBlock = (): MatDialogRef<TimeBlockEditorDialogComponent, TimeBlockEditorDialogResult> => {

    const input: TimeBlockEditorDialogInput = {
      taskName: this.input.timeBlock.parentTask.name,
      startTime: moment(this.input.timeBlock.startTime),
      endTime: this.input.timeBlock.endTaskEventId && moment(this.input.timeBlock.endTime) || null,
      lowerBound: this.input.timeBlock.editLowerBound && moment(this.input.timeBlock.editLowerBound) || null,
      upperBound: this.input.timeBlock.editUpperBound && moment(this.input.timeBlock.editUpperBound) || null,
    };

    const handle = this.dialog.open<TimeBlockEditorDialogComponent, TimeBlockEditorDialogInput, TimeBlockEditorDialogResult>(
      TimeBlockEditorDialogComponent, { data: input });

    handle.afterClosed().subscribe(async (result?: TimeBlockEditorDialogResult) => {
      if (result) {
        await this.apiService.updateTimeBlock(
          this.input.timeBlock.startTaskEventId,
          result.startTime,
          this.input.timeBlock.endTaskEventId || undefined,
          result.endTime || undefined,
        );
        await this.input.onTimeBlockEdited();
      }
    });
    return handle;
  }


  update = (input: LinkedTimeBlock): void => {
    if (input) {
      this.input.timeBlock = input;
      this.changeDetector.markForCheck();
    }
  }

  close = (): void => {
    this.bottomSheetRef.dismiss();
  }
}
